import * as React from "react";
import { X } from "lucide-react";
import { Badge } from "./badge";
import { Input } from "./input";
import { cn } from "@/lib/utils";

export function TagInput({
  value = [],
  onChange,
  placeholder = "Type and press Enter...",
  tagVariant = "default",
  maxTags = 50,
  className,
  disabled = false,
  ...props
}) {
  const [inputValue, setInputValue] = React.useState("");
  const inputRef = React.useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const newTag = inputValue.trim();
      
      // Don't add duplicates
      if (!value.includes(newTag) && value.length < maxTags) {
        onChange([...value, newTag]);
      }
      setInputValue("");
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      // Remove last tag on backspace if input is empty
      onChange(value.slice(0, -1));
    }
  };

  const removeTag = (tagToRemove) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  const getTagStyle = () => {
    switch (tagVariant) {
      case "skill":
        return "bg-blue-100 text-blue-700 hover:bg-blue-200";
      case "language":
        return "bg-purple-100 text-purple-700 hover:bg-purple-200";
      case "secondary":
        return "bg-slate-100 text-slate-700 hover:bg-slate-200";
      case "colorful":
        return "bg-amber-100 text-amber-700 hover:bg-amber-200";
      default:
        return "bg-slate-100 text-slate-700 hover:bg-slate-200";
    }
  };

  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 p-2 border rounded-md bg-white min-h-[42px] focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-1",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((tag, idx) => (
        <Badge
          key={idx}
          className={cn(
            "flex items-center gap-1 px-2 py-1 text-sm cursor-default",
            getTagStyle()
          )}
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(tag);
              }}
              className="ml-1 hover:bg-black/10 rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </Badge>
      ))}
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? placeholder : ""}
        disabled={disabled}
        className="flex-1 min-w-[100px] border-0 p-0 h-7 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
        {...props}
      />
    </div>
  );
}
